import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const ConversationalAIPage = () => {
  const [charCount, setCharCount] = useState(0);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [contactMethod, setContactMethod] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };

  const handleServiceToggle = (service: string) => {
    setSelectedServices(prev =>
      prev.includes(service)
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const handleContactMethodChange = (method: string) => {
    setContactMethod(method);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Add selected services
    formData.delete('services');
    selectedServices.forEach(service => {
      formData.append('services', service);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5695qe1ia1uhnel1ppg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setShowSuccess(true);
        setShowError(false);
        form.reset();
        setCharCount(0);
        setSelectedServices([]);
        setContactMethod('');
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        setShowError(true);
        setShowSuccess(false);
      }
    } catch (error) {
      setShowError(true);
      setShowSuccess(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm">
            <a href="/" className="text-gray-500 hover:text-blue-600 flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center mx-2 text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-500 hover:text-blue-600">Generative AI</a>
            <div className="w-4 h-4 flex items-center justify-center mx-2 text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-blue-600 font-medium">Conversational AI Systems</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full h-[500px]">
        <img
          src="https://public.readdy.ai/ai/img_res/aba66ae97d864ca3af4173079442b7ec.jpg"
          alt="Conversational AI Systems"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative h-full flex items-center">
          <div className="w-full max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Conversational AI Systems
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Transform your customer interactions with intelligent conversational systems that understand, engage, and deliver personalized experiences through natural language.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-blue-700 transition-colors">
                Get Started
              </button>
              <button className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:border-blue-600 hover:text-blue-600 transition-colors">
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Conversational AI Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our advanced conversational AI solutions help businesses automate customer support, enhance user engagement, streamline operations, and create innovative communication experiences through intelligent dialogue systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Chatbot Development */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20chatbot%20interface%20with%20friendly%20AI%20assistant%20avatar%20engaging%20in%20natural%20conversation%20on%20multiple%20devices%20including%20smartphone%20tablet%20and%20laptop%20with%20clean%20messaging%20bubbles%20and%20intuitive%20user%20interface%20in%20professional%20blue%20technology%20theme&width=400&height=300&seq=conv-ai-chatbot-001&orientation=landscape"
                  alt="Chatbot Development"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-chat-3-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Chatbot Development</h3>
                <p className="text-gray-600 mb-4">
                  Create intelligent chatbots that engage users in natural conversations across websites, apps, and messaging platforms.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Multi-platform deployment</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Contextual understanding</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Seamless human handoff</span>
                  </li>
                </ul>
                <a href="#" className="text-blue-600 font-medium flex items-center hover:text-blue-700">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Virtual Assistants */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Sophisticated%20virtual%20assistant%20hologram%20with%20AI%20robot%20helper%20providing%20personalized%20recommendations%20and%20task%20automation%20with%20digital%20interface%20showing%20calendar%20scheduling%20email%20management%20and%20smart%20suggestions%20in%20modern%20blue%20technology%20environment&width=400&height=300&seq=conv-ai-virtual-001&orientation=landscape"
                  alt="Virtual Assistants"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-robot-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Virtual Assistants</h3>
                <p className="text-gray-600 mb-4">
                  Implement sophisticated virtual assistants that handle complex tasks, provide personalized recommendations, and learn from interactions.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Task automation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Personalized experiences</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Continuous learning</span>
                  </li>
                </ul>
                <a href="#" className="text-blue-600 font-medium flex items-center hover:text-blue-700">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Voice Interfaces */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Voice%20recognition%20technology%20with%20sound%20wave%20visualization%20and%20microphone%20icon%20showing%20speech-to-text%20conversion%20with%20natural%20language%20processing%20on%20smart%20speaker%20and%20mobile%20devices%20in%20clean%20blue%20technology%20interface&width=400&height=300&seq=conv-ai-voice-001&orientation=landscape"
                  alt="Voice Interfaces"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-mic-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Voice Interfaces</h3>
                <p className="text-gray-600 mb-4">
                  Create intuitive voice-enabled systems that understand spoken language, respond naturally, and integrate with existing platforms.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Speech recognition</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Natural voice synthesis</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Multi-device integration</span>
                  </li>
                </ul>
                <a href="#" className="text-blue-600 font-medium flex items-center hover:text-blue-700">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Natural Language Processing */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Natural%20language%20processing%20visualization%20with%20text%20analysis%20sentiment%20detection%20and%20multilingual%20translation%20showing%20AI%20understanding%20human%20language%20with%20word%20clouds%20neural%20networks%20and%20language%20symbols%20in%20modern%20blue%20technology%20theme&width=400&height=300&seq=conv-ai-nlp-001&orientation=landscape"
                  alt="Natural Language Processing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-translate-2-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Natural Language Processing</h3>
                <p className="text-gray-600 mb-4">
                  Leverage advanced NLP to extract meaning from text, analyze sentiment, and enable more natural human-computer interactions.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Intent recognition</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Sentiment analysis</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Multilingual support</span>
                  </li>
                </ul>
                <a href="#" className="text-blue-600 font-medium flex items-center hover:text-blue-700">
                  Learn More
                  <div className="w-4 h-4 flex items-center justify-center ml-1">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Conversational AI Implementation Process</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              We follow a structured, collaborative approach to deliver conversational AI solutions that address your specific business needs and seamlessly integrate with your existing systems.
            </p>
          </div>

          <div className="relative">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -translate-y-1/2 hidden md:block"></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {[
                {
                  number: 1,
                  icon: 'ri-search-line',
                  title: 'Requirements Analysis',
                  description: 'We analyze your communication needs, user personas, and integration requirements to define clear project objectives.'
                },
                {
                  number: 2,
                  icon: 'ri-layout-2-line',
                  title: 'Solution Design',
                  description: 'We design a comprehensive architecture that incorporates the right conversational AI models and interfaces for your specific needs.'
                },
                {
                  number: 3,
                  icon: 'ri-code-s-slash-line',
                  title: 'Development & Training',
                  description: 'Our engineers develop and train custom conversational models using your data to ensure high accuracy and relevance.'
                },
                {
                  number: 4,
                  icon: 'ri-test-tube-line',
                  title: 'Testing & Optimization',
                  description: 'We rigorously test the solution with diverse conversation scenarios to ensure reliability, accuracy, and natural interactions.'
                },
                {
                  number: 5,
                  icon: 'ri-install-line',
                  title: 'Deployment & Integration',
                  description: 'We seamlessly deploy and integrate your conversational AI solution into your existing infrastructure with minimal disruption.'
                }
              ].map((step) => (
                <div key={step.number} className="relative">
                  <div className="hidden md:flex absolute top-0 left-1/2 w-10 h-10 bg-blue-600 rounded-full -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center text-white font-semibold">
                    {step.number}
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 relative z-0 h-full">
                    <div className="md:hidden w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold mb-4">
                      {step.number}
                    </div>
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                      <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                        <i className={step.icon}></i>
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Industry Applications</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Discover how our conversational AI solutions can transform operations and create new opportunities across various industries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Customer Service */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20customer%20service%20representative%20with%20headset%20working%20at%20modern%20help%20desk%20with%20multiple%20computer%20screens%20showing%20customer%20support%20tickets%20chat%20conversations%20and%20satisfaction%20ratings%20in%20bright%20office%20environment&width=400&height=300&seq=conv-ai-customer-001&orientation=landscape"
                  alt="Customer Service"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-customer-service-2-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Customer Service</h3>
                <p className="text-gray-600 mb-4">
                  Transform customer support with intelligent virtual agents that handle inquiries, resolve issues, and provide 24/7 assistance.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Automated ticket resolution</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Intelligent call routing</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Self-service knowledge base</span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-1">
                    <div className="w-4 h-4 flex items-center justify-center text-blue-600 mr-1">
                      <i className="ri-line-chart-line"></i>
                    </div>
                    <span className="font-medium text-gray-800 text-sm">Key Benefit</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Up to 70% reduction in support costs and 35% increase in customer satisfaction scores.
                  </p>
                </div>
              </div>
            </div>

            {/* Healthcare */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20healthcare%20technology%20with%20doctor%20using%20tablet%20showing%20patient%20appointment%20scheduling%20medication%20reminders%20and%20health%20monitoring%20dashboard%20in%20clean%20medical%20facility%20with%20digital%20health%20records%20interface&width=400&height=300&seq=conv-ai-healthcare-001&orientation=landscape"
                  alt="Healthcare"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-heart-pulse-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Healthcare</h3>
                <p className="text-gray-600 mb-4">
                  Enhance patient care with conversational systems that assist with scheduling, medication reminders, and health information.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Appointment scheduling</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Medication adherence</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Symptom assessment</span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-1">
                    <div className="w-4 h-4 flex items-center justify-center text-blue-600 mr-1">
                      <i className="ri-line-chart-line"></i>
                    </div>
                    <span className="font-medium text-gray-800 text-sm">Key Benefit</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Up to 40% reduction in administrative tasks and 25% improvement in patient engagement.
                  </p>
                </div>
              </div>
            </div>

            {/* Banking & Finance */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20banking%20technology%20with%20financial%20professional%20using%20digital%20tablet%20showing%20account%20management%20transaction%20processing%20and%20fraud%20detection%20dashboard%20in%20contemporary%20bank%20office%20with%20secure%20financial%20interface&width=400&height=300&seq=conv-ai-banking-001&orientation=landscape"
                  alt="Banking & Finance"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-bank-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Banking & Finance</h3>
                <p className="text-gray-600 mb-4">
                  Streamline financial services with conversational AI that handles account inquiries, transactions, and personalized financial advice.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Account management</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Transaction processing</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Fraud detection alerts</span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-1">
                    <div className="w-4 h-4 flex items-center justify-center text-blue-600 mr-1">
                      <i className="ri-line-chart-line"></i>
                    </div>
                    <span className="font-medium text-gray-800 text-sm">Key Benefit</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Up to 60% reduction in call center volume and 45% faster resolution of customer inquiries.
                  </p>
                </div>
              </div>
            </div>

            {/* E-commerce */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20e-commerce%20shopping%20experience%20with%20customer%20using%20smartphone%20for%20online%20shopping%20showing%20product%20recommendations%20order%20tracking%20and%20personalized%20shopping%20assistant%20in%20bright%20retail%20environment%20with%20digital%20interface&width=400&height=300&seq=conv-ai-ecommerce-001&orientation=landscape"
                  alt="E-commerce"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                    <i className="ri-shopping-bag-line"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">E-commerce</h3>
                <p className="text-gray-600 mb-4">
                  Enhance shopping experiences with conversational assistants that provide product recommendations, order tracking, and support.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Product discovery</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Order management</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    <span className="text-gray-700">Personalized recommendations</span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-1">
                    <div className="w-4 h-4 flex items-center justify-center text-blue-600 mr-1">
                      <i className="ri-line-chart-line"></i>
                    </div>
                    <span className="font-medium text-gray-800 text-sm">Key Benefit</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Up to 30% increase in conversion rates and 25% higher average order value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Technical Capabilities</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our conversational AI expertise spans a wide range of technologies, frameworks, and platforms to deliver solutions that meet your specific requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold mb-6">Supported Technologies & Frameworks</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-openai-fill', name: 'OpenAI GPT' },
                  { icon: 'ri-google-line', name: 'Dialogflow' },
                  { icon: 'ri-microsoft-line', name: 'Azure Bot Service' },
                  { icon: 'ri-amazon-line', name: 'Amazon Lex' },
                  { icon: 'ri-chat-1-line', name: 'RASA' },
                  { icon: 'ri-facebook-fill', name: 'Wit.ai' }
                ].map((tech) => (
                  <div key={tech.name} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
                      <div className="w-6 h-6 flex items-center justify-center text-blue-600">
                        <i className={tech.icon}></i>
                      </div>
                    </div>
                    <span className="text-gray-800 font-medium">{tech.name}</span>
                  </div>
                ))}
              </div>

              <h3 className="text-xl font-semibold mb-6 mt-10">Language Models & Techniques</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: 'ri-bubble-chart-line',
                    title: 'Large Language Models',
                    description: 'Advanced models trained on vast text corpora for natural language understanding and generation.'
                  },
                  {
                    icon: 'ri-radar-line',
                    title: 'Intent Recognition',
                    description: 'Sophisticated algorithms that accurately identify user intentions from natural language inputs.'
                  },
                  {
                    icon: 'ri-compasses-2-line',
                    title: 'Contextual Understanding',
                    description: 'Systems that maintain conversation context for more natural and coherent interactions.'
                  },
                  {
                    icon: 'ri-voice-recognition-line',
                    title: 'Speech Recognition & Synthesis',
                    description: 'Advanced voice processing for natural-sounding conversations and accurate transcription.'
                  }
                ].map((technique) => (
                  <div key={technique.title} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                        <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                          <i className={technique.icon}></i>
                        </div>
                      </div>
                      <span className="text-gray-800 font-medium">{technique.title}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{technique.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-6">Integration Capabilities</h3>
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 mb-6">
                <h4 className="font-semibold text-lg mb-4">Communication Channels</h4>
                <div className="space-y-3">
                  {[
                    'Websites & Web Applications',
                    'Mobile Apps (iOS, Android)',
                    'Messaging Platforms (WhatsApp, Messenger, Slack)',
                    'Voice Assistants & IVR Systems'
                  ].map((channel) => (
                    <div key={channel} className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <div>
                        <span className="text-gray-700">{channel}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <h4 className="font-semibold text-lg mb-4">Security & Privacy Features</h4>
                <div className="space-y-3">
                  {[
                    'End-to-End Encryption for Conversations',
                    'Data Anonymization & Minimization',
                    'GDPR & CCPA Compliant Implementation',
                    'Ethical AI & Bias Mitigation'
                  ].map((feature) => (
                    <div key={feature} className="flex items-start">
                      <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                        <i className="ri-check-line"></i>
                      </div>
                      <div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Performance Metrics</h3>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <div className="space-y-4">
                    {[
                      { label: 'Response Accuracy', value: 96 },
                      { label: 'User Satisfaction', value: 92 },
                      { label: 'Intent Recognition', value: 94 },
                      { label: 'Conversation Completion', value: 88 }
                    ].map((metric) => (
                      <div key={metric.label}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{metric.label}</span>
                          <span className="text-sm font-medium text-blue-600">{metric.value}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${metric.value}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA & Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business with Conversational AI?</h2>
              <p className="text-gray-600 mb-8">
                Contact our team of conversational AI experts to discuss your business challenges and how our intelligent dialogue systems can help you achieve your goals.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  {
                    icon: 'ri-customer-service-2-line',
                    title: 'Expert Consultation',
                    description: 'Our conversational AI specialists will work with you to understand your specific needs and recommend the right solutions.'
                  },
                  {
                    icon: 'ri-file-list-3-line',
                    title: 'Custom Solution Design',
                    description: "We'll create a tailored implementation plan that aligns with your business objectives and technical requirements."
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Ongoing Support',
                    description: 'We provide comprehensive support and maintenance to ensure your conversational AI solutions continue to deliver value.'
                  }
                ].map((item) => (
                  <div key={item.title} className="flex items-start">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <div className="w-5 h-5 flex items-center justify-center text-blue-600">
                        <i className={item.icon}></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+31616300011"
                  className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg whitespace-nowrap font-medium flex items-center justify-center hover:border-blue-600 hover:text-blue-600 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  +31 (0)616300011
                </a>
                <a
                  href="mailto:sales@thyamis.com"
                  className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg whitespace-nowrap font-medium flex items-center justify-center hover:border-blue-600 hover:text-blue-600 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  sales@thyamis.com
                </a>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
              <h3 className="text-2xl font-bold mb-6">Request a Consultation</h3>
              <form id="consultation-form" data-readdy-form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      id="company"
                      name="company"
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                      placeholder="Your company"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                      Contact Person
                    </label>
                    <input
                      id="name"
                      name="name"
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                      placeholder="your.email@company.com"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="requirements" className="block text-gray-700 font-medium mb-2">
                    Conversational AI Requirements
                  </label>
                  <textarea
                    id="requirements"
                    name="requirements"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                    placeholder="Please describe your conversational AI needs or business challenges"
                    onChange={handleTextareaChange}
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    <span>{charCount}</span>/500 characters
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">Interested Services</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { id: 'chatbot-dev', value: 'Chatbot Development', label: 'Chatbot Development' },
                      { id: 'virtual-assistant', value: 'Virtual Assistants', label: 'Virtual Assistants' },
                      { id: 'voice-interfaces', value: 'Voice Interfaces', label: 'Voice Interfaces' },
                      { id: 'nlp-solutions', value: 'NLP Solutions', label: 'NLP Solutions' }
                    ].map((service) => (
                      <div key={service.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={service.id}
                          name="services"
                          value={service.value}
                          className="hidden"
                          checked={selectedServices.includes(service.value)}
                          onChange={() => handleServiceToggle(service.value)}
                        />
                        <label htmlFor={service.id} className="flex items-center cursor-pointer">
                          <div className="w-5 h-5 flex items-center justify-center text-blue-600 mt-0.5 mr-2">
                            {selectedServices.includes(service.value) && (
                              <div className="w-3 h-3 bg-blue-600 rounded"></div>
                            )}
                          </div>
                          <span className="text-gray-700">{service.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">Preferred Contact Method</label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { id: 'contact-email', value: 'Email', label: 'Email' },
                      { id: 'contact-phone', value: 'Phone', label: 'Phone' },
                      { id: 'contact-meeting', value: 'Video Meeting', label: 'Video Meeting' }
                    ].map((method) => (
                      <div key={method.id} className="flex items-center">
                        <input
                          type="radio"
                          id={method.id}
                          name="contact-method"
                          value={method.value}
                          className="hidden"
                          checked={contactMethod === method.value}
                          onChange={() => handleContactMethodChange(method.value)}
                        />
                        <label htmlFor={method.id} className="flex items-center cursor-pointer">
                          <div className="w-5 h-5 border border-gray-300 rounded-full mr-2 flex items-center justify-center">
                            {contactMethod === method.value && (
                              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                            )}
                          </div>
                          <span className="text-gray-700">{method.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium w-full hover:bg-blue-700 transition-colors"
                >
                  Submit Request
                </button>

                {(showSuccess || showError) && (
                  <div className="mt-4">
                    {showSuccess && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
                        <div className="flex items-center">
                          <div className="w-5 h-5 flex items-center justify-center mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span>
                            Thank you! Your consultation request has been submitted successfully. We'll contact you within 24 hours.
                          </span>
                        </div>
                      </div>
                    )}
                    {showError && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
                        <div className="flex items-center">
                          <div className="w-5 h-5 flex items-center justify-center mr-2">
                            <i className="ri-error-warning-line"></i>
                          </div>
                          <span>There was an error submitting your request. Please try again.</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConversationalAIPage;
